<script lang="ts">
  import Modal from '../base/Modal.svelte';
  import CraftItem from '../components/CraftItem.svelte';
  import { Crafts } from '@game/database/CraftDB';
  // no itemsDB needed here
  import { profileStore } from '@stores/game/profile';
  import { onMount, onDestroy } from 'svelte';
  import { gameEventBus } from '@game/events';
  import { audioManager } from '@engine/audio';

  interface Props {
    isOpen?: boolean;
    onCraftStarted?: (data: { craftId: string; quantity: number }) => void;
  }

  let {
    isOpen = $bindable(false),
    onCraftStarted = (_data: { craftId: string; quantity: number }) => {},
  }: Props = $props();

  // Реактивные источники данных
  const profile = $derived($profileStore);
  const availableCrafts = $derived.by(() => {
    profile; // пересчитываем при изменениях профиля
    return Crafts.getAvailableCrafts();
  });
  const activeCraftsReadable = Crafts.activeCraftsStore();
  const activeCrafts = $derived($activeCraftsReadable);

  let unsubscribeEvents: (() => void)[] = [];
  let rafId: number | null = null;

  onMount(() => {
    // Подписываемся на события открытия/закрытия модала
    unsubscribeEvents.push(
      gameEventBus.on('production-open', () => {
        isOpen = true;
        audioManager.playSFX('ui_click', { volume: 0.5 });
        startProgressTimer();
      }),
      gameEventBus.on('production-close', () => {
        isOpen = false;
        audioManager.playSFX('ui_click', { volume: 0.3 });
        stopProgressTimer();
      })
    );

    // Запускаем таймер если модал уже открыт
    if (isOpen) {
      startProgressTimer();
    }
  });

  onDestroy(() => {
    unsubscribeEvents.forEach(fn => fn());
    stopProgressTimer();
  });

  function startProgressTimer() {
    if (rafId) return;

    const tick = () => {
      // Читаем activeCrafts чтобы реактивно перерисовываться
      activeCrafts;
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
  }

  function stopProgressTimer() {
    if (rafId) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  }

  function handleClose() {
    gameEventBus.emit('production-close');
  }

  // helpers больше не нужны после удаления Active-секции

  // Просто используем списки без группировки
</script>

<Modal {isOpen} title="Production" size="lg" maxHeightVh={85} on:close={handleClose}>
  <div class="production-container">
    {#if availableCrafts.length === 0}
      <div class="empty-state">
        <div class="empty-icon">🔒</div>
        <h3>No Production Available</h3>
        <p>Reach higher levels to unlock more crafting recipes!</p>
      </div>
    {:else}
      <div class="production-content">
        <!-- Available Crafts Section -->
        <div class="available-crafts-section">
          <div class="crafts-list">
            {#each availableCrafts as craft (craft.craftId)}
              <CraftItem {craft} {onCraftStarted} />
            {/each}
          </div>
        </div>
      </div>
    {/if}
  </div>
</Modal>

<style lang="postcss">
  @reference "@/styles/theme.css";

  .production-container {
    @apply w-full h-full;
  }

  .empty-state {
    @apply flex flex-col items-center justify-center;
    @apply py-16 px-8;
    @apply text-center;
  }

  .empty-icon {
    @apply text-6xl mb-4;
    @apply opacity-50;
  }

  .empty-state h3 {
    @apply text-xl font-bold text-neon-cyan mb-2;
  }

  .empty-state p {
    @apply text-gray-400;
  }

  .production-content {
    @apply space-y-6;
    @apply max-h-96 overflow-y-auto;
    @apply pr-2;
  }

  /* removed Active Production styles */

  .crafts-list {
    @apply space-y-2;
  }

  /* Scrollbar styling */
  .production-content::-webkit-scrollbar {
    @apply w-2;
  }

  .production-content::-webkit-scrollbar-track {
    @apply bg-dark-bg/30;
    @apply rounded;
  }

  .production-content::-webkit-scrollbar-thumb {
    @apply bg-neon-green/30;
    @apply rounded;
  }

  .production-content::-webkit-scrollbar-thumb:hover {
    @apply bg-neon-green/50;
  }
</style>
